import { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useModuleStore } from '../../stores/moduleStore'
import { useProgressStore } from '../../stores/progressStore'
import { caseStudyService, moduleService } from '../../services'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import achievementToast from '../../components/AchievementToast'
import CelebrationModal from '../../components/CelebrationModal'
import ReadingProgressTracker from '../../components/modules/ReadingProgressTracker'
import FourCTooltip from '../../components/common/FourCTooltip'
import ModuleQuiz from '../../components/modules/ModuleQuiz'
import DiscussionList from '../../components/discussions/DiscussionList'
import FileUpload from '../../components/capstones/FileUpload'
import toast from 'react-hot-toast'
import {
  BookOpenIcon,
  ClockIcon,
  AcademicCapIcon,
  BeakerIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'

export default function ModuleView() {
  const { id } = useParams()
  const { currentModule, loading, error, fetchModule, clearCurrentModule } = useModuleStore()
  const { fetchProgress, getCompletionPercentage } = useProgressStore()
  const [completing, setCompleting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const contentRef = useRef(null)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false)
  const [earnedBadges, setEarnedBadges] = useState([])
  
  // Exercise submission state
  const [exerciseAnswer, setExerciseAnswer] = useState('')
  const [exerciseNotes, setExerciseNotes] = useState('')
  const [draftSubmissionId, setDraftSubmissionId] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [existingSubmission, setExistingSubmission] = useState(null)
  const [submittingExercise, setSubmittingExercise] = useState(false)
  const [showExerciseForm, setShowExerciseForm] = useState(false)

  useEffect(() => {
    fetchModule(id)
    return () => clearCurrentModule()
  }, [id, fetchModule, clearCurrentModule])

  useEffect(() => {
    if (currentModule?.user_progress) {
      setIsCompleted(currentModule.user_progress.status === 'completed')
    }
  }, [currentModule])

  // Load existing exercise submission
  useEffect(() => {
    const loadExerciseSubmission = async () => {
      try {
        const data = await moduleService.getExerciseSubmission(id)
        if (data.submission) {
          setExistingSubmission(data.submission)
          setExerciseAnswer(data.submission.content || '')
          setExerciseNotes(data.submission.notes || '')
          setUploadedFiles(data.submission.files || [])
        }
      } catch (err) {
        // No submission yet - that's okay
      }
    }
    
    if (id) {
      loadExerciseSubmission()
    }
  }, [id])

  const handleShowExerciseForm = async () => {
    if (!showExerciseForm) {
      try {
        const data = await moduleService.getDraftSubmission(id)
        setDraftSubmissionId(data.submission.id)
        setShowExerciseForm(true)
      } catch (err) {
        console.error('Failed to create draft:', err)
        toast.error('Failed to open submission form')
      }
    } else {
      setShowExerciseForm(false)
    }
  }

  const handleSubmitExercise = async () => {
    if (!exerciseAnswer.trim()) {
      toast.error('Please provide your exercise answer')
      return
    }

    try {
      setSubmittingExercise(true)
      const response = await moduleService.submitExercise(id, {
        content: exerciseAnswer,
        notes: exerciseNotes,
      })
      
      toast.success(response.message || 'Exercise submitted successfully!')
      setExistingSubmission(response.submission)
      setShowExerciseForm(false)
    } catch (err) {
      console.error('Failed to submit exercise:', err)
      toast.error(err.response?.data?.message || 'Failed to submit exercise')
    } finally {
      setSubmittingExercise(false)
    }
  }

  const handleComplete = async () => {
    if (completing || isCompleted) return

    try {
      setCompleting(true)
      const response = await moduleService.updateProgress(id, { status: 'completed', progress_percentage: 100 })
      
      // Show module completion toast
      achievementToast.moduleComplete(currentModule.title)

      // Check for earned badges from the response
      if (response.earned_badges && response.earned_badges.length > 0) {
        setEarnedBadges(response.earned_badges)
        
        // Show individual badge toasts
        response.earned_badges.forEach((badge, index) => {
          setTimeout(() => {
            achievementToast.badge(badge)
          }, (index + 1) * 1500) // Stagger badge notifications
        })

        // Show celebration modal after a delay
        setTimeout(() => {
          setShowCelebrationModal(true)
        }, response.earned_badges.length * 1500 + 1000)
      }

      setIsCompleted(true)
      
      // Refetch module and overall progress
      await fetchModule(id)
      await fetchProgress()

      // Check for milestone achievements
      const newCompletionPercentage = getCompletionPercentage()
      if ([25, 50, 75, 100].includes(newCompletionPercentage)) {
        setTimeout(() => {
          achievementToast.milestone(newCompletionPercentage)
        }, response.earned_badges?.length ? response.earned_badges.length * 1500 + 2000 : 2000)
      }
    } catch (err) {
      console.error('Failed to mark module as complete:', err)
      achievementToast.error('Failed to mark module as complete. Please try again.')
    } finally {
      setCompleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Loading module...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    )
  }

  if (!currentModule) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Module not found</p>
        <Link to="/modules" className="text-cte-blue hover:underline mt-4 inline-block">
          Back to modules
        </Link>
      </div>
    )
  }

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800'
      case 'advanced':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="max-w-4xl" ref={contentRef}>
      {/* Back Button */}
      <Link
        to="/modules"
        className="inline-flex items-center text-cte-blue hover:text-cte-blue-dark mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Modules
      </Link>

      {/* Getting Started Banner - Only show if not completed */}
      {!isCompleted && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <BookOpenIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🎯 Getting Started with This Module
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">1</span>
                  <p><strong>Read</strong> through the module content below - take your time!</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-xs">2</span>
                  <p><strong>Explore</strong> the case studies to see real-world applications</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-xs">3</span>
                  <p><strong>Complete</strong> the labs and exercises to practice what you've learned</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-bold text-xs">4</span>
                  <p><strong>Submit</strong> your homework below when you're ready</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ClockIcon className="w-4 h-4" />
                  <span>Estimated time: {currentModule.estimated_hours} hours</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ChartBarIcon className="w-4 h-4" />
                  <span>Progress: {currentModule?.user_progress?.completion_percentage || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How to Complete This Module - Always visible */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
            <QuestionMarkCircleIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              How to Complete This Module
              {isCompleted && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                  <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                  Completed
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpenIcon className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Module Content</h4>
                </div>
                <p className="text-gray-700">Read and understand the main lesson material. Your reading progress is tracked automatically as you scroll.</p>
              </div>
              
              <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <BeakerIcon className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">Labs (Practice)</h4>
                </div>
                <p className="text-gray-700">Complete hands-on exercises to apply what you've learned. Check the Labs tab above.</p>
              </div>
              
              <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-semibold text-gray-900">Case Studies</h4>
                </div>
                <p className="text-gray-700">Study real-world examples and applications. Find these in the Case Studies tab.</p>
              </div>
              
              <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <PencilSquareIcon className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-semibold text-gray-900">Homework Submission</h4>
                </div>
                <p className="text-gray-700">Submit your exercise answers below. This is required to mark the module as complete.</p>
              </div>
            </div>
            
            <div className="mt-4 bg-white/80 rounded-lg p-4 border-2 border-green-300">
              <div className="flex items-start gap-3">
                <LightBulbIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">To Mark as Complete:</h4>
                  <ul className="space-y-1 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <ArrowRightIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Read through the entire module content (tracked automatically)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRightIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Complete and submit the module homework/exercise below</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRightIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Click "Mark as Complete" button when you're done</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reading Progress Tracker */}
      <ReadingProgressTracker 
        moduleId={id} 
        initialProgress={currentModule?.user_progress?.metadata?.reading_percentage || 0}
        initialScrollPosition={currentModule?.user_progress?.scroll_position || 0}
      />

      {/* Module Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm font-mono text-cte-blue mb-2">
              {currentModule.code}
            </div>
            <h1 className="text-3xl font-bold text-cte-black mb-4 inline-flex items-center">
              {currentModule.title}
              {currentModule.title.includes('4C') && <FourCTooltip inline />}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {currentModule.description}
            </p>

            {/* Meta Information */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center text-gray-600">
                <ClockIcon className="w-5 h-5 mr-2" />
                <span>{currentModule.estimated_hours} hours</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(currentModule.difficulty_level)}`}>
                {currentModule.difficulty_level}
              </span>
            </div>
          </div>

          {/* Progress Circle */}
          {currentModule?.user_progress && (
            <div className="flex-shrink-0 ml-6">
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (currentModule.user_progress.completion_percentage || 0) / 100)}`}
                    className="text-blue-600 transition-all duration-500"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-gray-900">
                    {Math.round(currentModule.user_progress.completion_percentage || 0)}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-center text-gray-600 mt-2">Complete</p>
            </div>
          )}
        </div>
      </div>

      {/* Learning Objectives */}
      {currentModule.learning_objectives && currentModule.learning_objectives.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-cte-black mb-4 flex items-center">
            <AcademicCapIcon className="w-6 h-6 mr-2 text-cte-blue" />
            Learning Objectives
          </h2>
          <ul className="space-y-2">
            {currentModule.learning_objectives.map((objective, index) => (
              <li key={index} className="flex items-start">
                <span className="text-cte-blue mr-3">•</span>
                <span className="text-gray-700">{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Module Content */}
      {currentModule.content && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-semibold text-cte-black flex items-center">
              <BookOpenIcon className="w-6 h-6 mr-2 text-cte-blue" />
              Module Content
            </h2>
            <div className="group relative">
              <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 hover:text-cte-blue cursor-help" />
              <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <p className="font-semibold mb-1">📖 Main Lesson Material</p>
                <p>Read through this content carefully. Your reading progress is automatically tracked as you scroll down the page.</p>
              </div>
            </div>
          </div>
          <div className="prose prose-slate max-w-none 
            prose-pre:overflow-x-auto prose-pre:whitespace-pre prose-pre:rounded-lg
            prose-code:whitespace-pre-wrap prose-code:break-words
            [&_pre]:overflow-x-auto [&_pre]:max-w-full">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {currentModule.content}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Exercise Submission */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-cte-black flex items-center">
            <PencilSquareIcon className="w-6 h-6 mr-2 text-cte-blue" />
            Module Exercise & Homework
          </h2>
          {existingSubmission && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              Submitted
            </span>
          )}
        </div>

        {/* Instructions for exercises */}
        {!existingSubmission && !showExerciseForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">📝 How to Submit Your Work</h4>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Complete the exercises described in the module content above</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Click "Submit Exercise Work" to open the submission form</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Paste your answers, code, or rewritten prompts in the text area</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Optionally upload files (screenshots, documents, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <span>Submit for instructor feedback (helps you learn and improve!)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {existingSubmission && !showExerciseForm ? (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Your Submission:</p>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-900 whitespace-pre-wrap">{existingSubmission.content}</p>
              </div>
              {existingSubmission.notes && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes:</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{existingSubmission.notes}</p>
                </div>
              )}
              {existingSubmission.status === 'graded' && existingSubmission.feedback && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Instructor Feedback:</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{existingSubmission.feedback}</p>
                  {existingSubmission.score !== null && (
                    <p className="text-sm font-semibold text-cte-blue mt-2">
                      Score: {existingSubmission.score}/{existingSubmission.max_score}
                    </p>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleShowExerciseForm}
              className="text-cte-blue hover:text-cte-blue/80 text-sm font-medium"
            >
              Update Submission
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {!showExerciseForm ? (
              <button
                onClick={handleShowExerciseForm}
                className="bg-cte-blue text-white px-6 py-2 rounded-lg hover:bg-cte-blue/90 transition-colors font-medium"
              >
                <PencilSquareIcon className="w-5 h-5 inline-block mr-2" />
                Submit Exercise Work
              </button>
            ) : (
              <div className="space-y-4">
                {/* Help tip for submission form */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>💡 Tip:</strong> Be thorough in your submission! Include your thought process, 
                    any challenges you faced, and what you learned. This helps your instructor provide better feedback.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Exercise Answer *
                  </label>
                  <textarea
                    value={exerciseAnswer}
                    onChange={(e) => setExerciseAnswer(e.target.value)}
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cte-blue focus:border-transparent"
                    placeholder="Paste your exercise work here (e.g., rewritten prompts, analysis, code, etc.)..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Include your completed exercises, answers, code, or any work requested in the module content
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={exerciseNotes}
                    onChange={(e) => setExerciseNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cte-blue focus:border-transparent"
                    placeholder="Add any notes, questions, or context for your instructor..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Share your thought process, challenges faced, or ask questions about the material
                  </p>
                </div>

                {draftSubmissionId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attach Files (Optional)
                    </label>
                    <FileUpload
                      submissionId={draftSubmissionId}
                      onFilesChange={setUploadedFiles}
                      existingFiles={uploadedFiles}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Upload screenshots, documents, or any supporting files for your submission
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleSubmitExercise}
                    disabled={submittingExercise || !exerciseAnswer.trim()}
                    className="bg-cte-blue text-white px-6 py-2 rounded-lg hover:bg-cte-blue/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingExercise ? 'Submitting...' : 'Submit for Review'}
                  </button>
                  <button
                    onClick={() => setShowExerciseForm(false)}
                    className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Knowledge Check Quiz */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-cte-black mb-4 flex items-center">
          <AcademicCapIcon className="w-6 h-6 mr-2 text-cte-blue" />
          Knowledge Check
        </h2>
        <ModuleQuiz moduleId={id} onComplete={handleComplete} />
      </div>

      {/* Labs */}
      {currentModule.labs && currentModule.labs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-semibold text-cte-black flex items-center">
              <BeakerIcon className="w-6 h-6 mr-2 text-cte-blue" />
              Labs ({currentModule.labs.length})
            </h2>
            <div className="group relative">
              <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 hover:text-cte-blue cursor-help flex-shrink-0" />
              <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <p className="font-semibold mb-1">🧪 Hands-On Practice</p>
                <p>Interactive exercises where you'll build, code, and experiment. Click any lab to get started with step-by-step instructions.</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {currentModule.labs.map((lab) => (
              <Link
                key={lab.id}
                to={`/labs/${lab.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:border-cte-blue hover:shadow-sm transition-all"
              >
                <h3 className="font-semibold text-cte-black mb-1">{lab.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{lab.description}</p>
                <div className="flex items-center text-sm text-gray-500">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  <span>{lab.estimated_minutes} minutes</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Case Studies (ENHANCED!) */}
      {currentModule.case_studies && currentModule.case_studies.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-cte-black flex items-center">
                <ChartBarIcon className="w-6 h-6 mr-2 text-cte-blue" />
                Case Studies ({currentModule.case_studies.length})
                <span className="ml-2 text-xs bg-gradient-to-r from-cte-blue to-blue-600 text-white px-3 py-1 rounded-full font-semibold">
                  Real-World Applications
                </span>
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Apply your knowledge to real-world scenarios and business problems
              </p>
            </div>
            <div className="group relative">
              <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 hover:text-cte-blue cursor-help flex-shrink-0" />
              <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <p className="font-semibold mb-1">💼 Real-World Examples</p>
                <p>Study how professionals apply these concepts in actual business scenarios. Click any case study to dive deeper.</p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {currentModule.case_studies.map((caseStudy) => {
              const isCompleted = caseStudy.progress?.status === 'completed'
              const difficultyColor = getDifficultyColor(caseStudy.difficulty_level)

              return (
                <Link
                  key={caseStudy.id}
                  to={`/case-studies/${caseStudy.id}`}
                  className="group relative border-2 border-gray-200 rounded-xl p-5 hover:border-cte-blue hover:shadow-lg transition-all duration-300 block"
                >
                  {isCompleted && (
                    <div className="absolute top-3 right-3">
                      <CheckCircleIconSolid className="h-6 w-6 text-green-500" />
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-cte-blue uppercase tracking-wide">
                        {caseStudy.code}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${difficultyColor}`}>
                        {caseStudy.difficulty_level}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-cte-blue transition-colors">
                      {caseStudy.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {caseStudy.scenario}
                    </p>
                  </div>

                  {/* Learning Outcomes */}
                  {caseStudy.learning_outcomes && caseStudy.learning_outcomes.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">You'll learn:</p>
                      <ul className="space-y-1">
                        {caseStudy.learning_outcomes.slice(0, 2).map((outcome, idx) => (
                          <li key={idx} className="flex items-start text-xs text-gray-600">
                            <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-1">{outcome}</span>
                          </li>
                        ))}
                        {caseStudy.learning_outcomes.length > 2 && (
                          <li className="text-xs text-gray-500 ml-4">
                            +{caseStudy.learning_outcomes.length - 2} more outcomes
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center text-xs text-gray-500">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      <span>{caseStudy.estimated_minutes} min</span>
                    </div>
                    <span className="text-sm font-semibold text-cte-blue group-hover:text-cte-blue-dark flex items-center">
                      {isCompleted ? 'Review →' : 'Start Case Study →'}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Module Discussions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-cte-black flex items-center">
              <ChatBubbleLeftRightIcon className="w-6 h-6 mr-2 text-cte-blue" />
              Discussions
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Ask questions, share insights, and collaborate with other learners.
            </p>
          </div>
          <div className="group relative">
            <QuestionMarkCircleIcon className="w-5 h-5 text-gray-400 hover:text-cte-blue cursor-help flex-shrink-0" />
            <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <p className="font-semibold mb-1">💬 Community Forum</p>
              <p>Ask questions, get help, share discoveries, and learn from other students. Your instructor monitors this too!</p>
            </div>
          </div>
        </div>
        <DiscussionList 
          discussionableType="Module" 
          discussionableId={currentModule.id} 
        />
      </div>

      {/* Mark as Complete Button */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {!isCompleted && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <LightBulbIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Before Marking as Complete:</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Read through all module content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Complete and submit the module exercise/homework above</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Review case studies and labs (if available)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Ask any questions in the discussion section</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {isCompleted ? 'Module Completed! 🎉' : 'Ready to continue?'}
            </h3>
            <p className="text-sm text-gray-600">
              {isCompleted 
                ? 'You\'ve completed this module. Great work!' 
                : 'Mark this module as complete once you\'ve finished the content.'}
            </p>
          </div>
          <button
            onClick={handleComplete}
            disabled={completing || isCompleted}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center transition-all ${
              isCompleted
                ? 'bg-green-100 text-green-700 cursor-not-allowed'
                : 'bg-cte-blue text-white hover:bg-cte-blue-dark hover:shadow-lg'
            } ${completing ? 'opacity-50 cursor-wait' : ''}`}
          >
            {completing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : isCompleted ? (
              <>
                <CheckCircleIconSolid className="w-5 h-5 mr-2" />
                Completed
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Mark as Complete
              </>
            )}
          </button>
        </div>
      </div>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebrationModal}
        onClose={() => setShowCelebrationModal(false)}
        badges={earnedBadges}
        title="Module Complete! 🎉"
        message={earnedBadges.length > 0 ? `You've unlocked ${earnedBadges.length} new badge${earnedBadges.length > 1 ? 's' : ''}!` : "Great job completing this module!"}
        confettiType="basic"
      />
    </div>
  )
}

